import { useEffect, useMemo } from 'react';

import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { useInMemoryCache } from '@/shared/hooks/useInMemoryCache';
import { useLogger } from '@/shared/hooks/useLogger';

import { createUserSettings } from '../lib/userSettings';

interface UpdateLanguageParams {
    onSuccess?: (code: string) => void;
    onError?: (error: any) => void;
}

function isAbortError(value: unknown): value is DOMException {
    return value instanceof DOMException && value.name === 'AbortError';
}

export function useUserSettings() {
    const inMemoryCache = useInMemoryCache();
    const graphqlClient = useGraphqlClient();
    const logger = useLogger();

    const settings = useMemo(() => {
        const abortController = new AbortController();

        const userSettings = createUserSettings({
            graphqlClient,
            inMemoryCache,
            abortSignal: abortController.signal,
        });

        return {
            preferredLanguage: userSettings.preferredLanguage,

            async updatePreferredLanguage(
                code: string,
                { onSuccess, onError }: UpdateLanguageParams = {}
            ) {
                try {
                    const result = await userSettings.updatePreferredLanguage(code);

                    if (result) {
                        onSuccess?.(result);
                    }

                    return result;
                } catch (error) {
                    if (isAbortError(error)) {
                        return null;
                    }

                    logger.error({ error }, 'Failed to update preferred language');

                    onError?.(error);
                }
            },

            cleanUp() {
                abortController.abort();
            },
        };
    }, [graphqlClient, inMemoryCache, logger]);

    useEffect(() => settings.cleanUp, [settings]);

    return settings;
}
